import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiGet } from '../services/apiFetch';
import { handleApiResponse } from '../services/apiErrorHandler';

const AdminDataContext = createContext();

export const useAdminData = () => {
    const context = useContext(AdminDataContext);
    if (!context) {
        throw new Error('useAdminData must be used within an AdminDataProvider');
    }
    return context;
};

export const AdminDataProvider = ({ children }) => {
    // Players state
    const [allPlayers, setAllPlayers] = useState([]);
    const [playersLoading, setPlayersLoading] = useState(true);
    const [playersError, setPlayersError] = useState(null);

    // Teams state
    const [allTeams, setAllTeams] = useState([]);
    const [teamsLoading, setTeamsLoading] = useState(true);
    const [teamsError, setTeamsError] = useState(null);

    // Leagues state
    const [allLeagues, setAllLeagues] = useState([]);
    const [leaguesLoading, setLeaguesLoading] = useState(true);
    const [leaguesError, setLeaguesError] = useState(null);

    // Overall loading state
    const loading = playersLoading || teamsLoading || leaguesLoading;

    // Fetch Players
    const fetchAllPlayers = async () => {
        try {
            setPlayersLoading(true);
            const response = await apiGet('/api/rl/players?image=true&dob=true&name=true');
            const handled = handleApiResponse(response);

            if (!handled.success) {
                throw new Error(handled.error.message);
            }

            console.log("admin panel fetched player data", handled.data);
            const playersDict = handled.data.data;
            setAllPlayers(playersDict);
            setPlayersError(null);
        } catch (err) {
            console.error('Error fetching all players:', err);
            setPlayersError(err);
        } finally {
            setPlayersLoading(false);
        }
    };

    // Fetch Teams
    const fetchAllTeams = async () => {
        try {
            setTeamsLoading(true);
            const response = await apiGet('/api/rl/teams');
            const handled = handleApiResponse(response);

            if (!handled.success) {
                throw new Error(handled.error.message);
            }

            console.log("admin panel fetched team data", handled.data);
            const teamsDict = handled.data.data;
            setAllTeams(teamsDict);
            setTeamsError(null);
        } catch (err) {
            console.error('Error fetching all teams:', err);
            setTeamsError(err);
        } finally {
            setTeamsLoading(false);
        }
    };

    // Fetch Leagues
    const fetchAllLeagues = async () => {
        try {
            setLeaguesLoading(true);
            const response = await apiGet('/api/rl/leagues');
            const handled = handleApiResponse(response);

            if (!handled.success) {
                throw new Error(handled.error.message);
            }

            console.log("admin panel fetched league data", handled.data);
            const leaguesDict = handled.data.data;
            setAllLeagues(leaguesDict);
            setLeaguesError(null);
        } catch (err) {
            console.error('Error fetching all leagues:', err);
            setLeaguesError(err);
        } finally {
            setLeaguesLoading(false);
        }
    };

    // Initialize all data
    useEffect(() => {
        const fetchAllData = async () => {
            await Promise.all([
                fetchAllPlayers(),
                fetchAllTeams(),
                fetchAllLeagues()
            ]);
        };

        fetchAllData();
    }, []);

    // Helper functions for players
    const getAvailablePlayers = (excludePlayerIds = []) => {
        return allPlayers.filter(player => {
            const playerId = player.id;
            return !excludePlayerIds.includes(playerId);
        });
    };

    const addPlayerToContext = (newPlayer) => {
        setAllPlayers(prev => [...prev, newPlayer]);
    };

    const updatePlayerInContext = (updatedPlayer) => {
        setAllPlayers(prev =>
            prev.map(player =>
                (player.id) === (updatedPlayer.id)
                    ? updatedPlayer
                    : player
            )
        );
    };

    const removePlayerFromContext = (playerId) => {
        setAllPlayers(prev =>
            prev.filter(player => (player.id) !== playerId)
        );
    };

    // Helper functions for teams
    const getAvailableTeams = (excludeTeamIds = []) => {
        return allTeams.filter(team => !excludeTeamIds.includes(team.id));
    };

    const addTeamToContext = (newTeam) => {
        setAllTeams(prev => [...prev, newTeam]);
    };

    const updateTeamInContext = (updatedTeam) => {
        setAllTeams(prev =>
            prev.map(team => team.id === updatedTeam.id ? updatedTeam : team)
        );
    };

    const removeTeamFromContext = (teamId) => {
        setAllTeams(prev => prev.filter(team => team.id !== teamId));
    };

    // Helper functions for leagues
    const addLeagueToContext = (newLeague) => {
        setAllLeagues(prev => [...prev, newLeague]);
    };

    const updateLeagueInContext = (updatedLeague) => {
        setAllLeagues(prev =>
            prev.map(league => league.id === updatedLeague.id ? updatedLeague : league)
        );
    };

    const removeLeagueFromContext = (leagueId) => {
        setAllLeagues(prev => prev.filter(league => league.id !== leagueId));
    };

    // Refresh functions
    const refreshPlayers = () => fetchAllPlayers();
    const refreshTeams = () => fetchAllTeams();
    const refreshLeagues = () => fetchAllLeagues();
    const refreshAllData = () => {
        fetchAllPlayers();
        fetchAllTeams();
        fetchAllLeagues();
    };

    const value = {
        // Data
        allPlayers,
        allTeams,
        allLeagues,

        // Loading states
        playersLoading,
        teamsLoading,
        leaguesLoading,
        loading, // overall loading

        // Error states
        playersError,
        teamsError,
        leaguesError,

        // Helper functions - Players
        getAvailablePlayers,
        addPlayerToContext,
        updatePlayerInContext,
        removePlayerFromContext,

        // Helper functions - Teams
        getAvailableTeams,
        addTeamToContext,
        updateTeamInContext,
        removeTeamFromContext,

        // Helper functions - Leagues
        addLeagueToContext,
        updateLeagueInContext,
        removeLeagueFromContext,

        // Refresh functions
        refreshPlayers,
        refreshTeams,
        refreshLeagues,
        refreshAllData
    };

    return (
        <AdminDataContext.Provider value={value}>
            {children}
        </AdminDataContext.Provider>
    );
}; 